import React, { useEffect, useState, useMemo } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function LibroMayor() {
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cuentas");
        const imputables = data.filter((c) => c.imputable);
        setCuentas(imputables);
        if (imputables[0]) setCuentaId(imputables[0].id);
      } catch (e) {
        toast.error(extractError(e));
      }
    })();
  }, []);

  useEffect(() => {
    if (!cuentaId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/libro-mayor/${cuentaId}`);
        setData(data);
      } catch (e) {
        toast.error(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [cuentaId]);

  const sortedCuentas = useMemo(
    () => [...cuentas].sort((a, b) => a.codigo.localeCompare(b.codigo)),
    [cuentas]
  );

  return (
    <Page>
      <PageHeader title="Libro mayor" subtitle="Movimientos detallados por cuenta" testid="libro-mayor-header"
        actions={cuentaId && (
          <div className="flex items-center gap-2">
            <button onClick={() => window.open(`${API_BASE}/libro-mayor/${cuentaId}/excel`, "_blank")}
              data-testid="lm-excel" className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>
            <button onClick={() => window.open(`${API_BASE}/libro-mayor/${cuentaId}/pdf`, "_blank")}
              data-testid="lm-pdf" className="inline-flex items-center gap-1.5 border border-destructive text-destructive hover:bg-destructive/10 text-sm px-3 py-1.5 rounded-sm">
              <FileText className="h-4 w-4" /> PDF
            </button>
          </div>
        )}
      />

      <div className="bg-card border border-border rounded-md p-4 mb-4">
        <label className="label-caps block mb-1.5">Cuenta</label>
        <Select value={cuentaId} onValueChange={setCuentaId}>
          <SelectTrigger data-testid="mayor-cuenta-select" className="max-w-md text-sm">
            <SelectValue placeholder="Seleccionar cuenta..." />
          </SelectTrigger>
          <SelectContent>
            {sortedCuentas.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                <span className="font-mono mr-2">{c.codigo}</span>{c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : !data ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Selecciona una cuenta</div>
        ) : (
          <>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="label-caps">Cuenta</div>
                <div className="font-display text-lg font-medium mt-0.5">
                  <span className="font-mono text-muted-foreground text-base mr-2">{data.cuenta.codigo}</span>
                  {data.cuenta.nombre}
                </div>
              </div>
              <div className="text-right">
                <div className="label-caps">Saldo final</div>
                <div className="num text-2xl font-semibold text-primary" data-testid="mayor-saldo-final">
                  {fmtCLP(data.saldo_final)}
                </div>
              </div>
            </div>
            {data.movimientos.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="mayor-empty">
                Sin movimientos
              </div>
            ) : (
              <table className="acc-table" data-testid="mayor-table">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Fecha</th>
                    <th style={{ width: 70 }} className="num">N°</th>
                    <th>Glosa</th>
                    <th className="num" style={{ width: 130 }}>Débito</th>
                    <th className="num" style={{ width: 130 }}>Crédito</th>
                    <th className="num" style={{ width: 140 }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.movimientos.map((m, i) => (
                    <tr key={i}>
                      <td>{fmtDate(m.fecha)}</td>
                      <td className="num">#{m.numero}</td>
                      <td>
                        {m.glosa}
                        {m.glosa_linea && <span className="text-[11px] text-muted-foreground/70 ml-2">— {m.glosa_linea}</span>}
                      </td>
                      <td className="num">{m.debito > 0 ? fmtCLP(m.debito) : ""}</td>
                      <td className="num">{m.credito > 0 ? fmtCLP(m.credito) : ""}</td>
                      <td className="num font-semibold">{fmtCLP(m.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
